import React, { useMemo } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Flex,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import ChipGroup from "./ChipGroup";
import type { ChipOption } from "./ChipGroup";
import type {
  DownloadFilters,
  DownloadOptions,
  DownloadParts,
  DownloadSubject,
} from "./downloads.types";

const NO_PARTS: DownloadParts = {
  od: false,
  quiz: false,
  quizVideo: false,
  odSummary: false,
};
const ONLY_OD_SUMMARY: DownloadParts = { ...NO_PARTS, odSummary: true };

const TRIMESTERS: ChipOption<number>[] = [1, 2, 3].map((trimester) => ({
  value: trimester,
  label: `T${trimester}`,
}));

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

// No grade chosen is every grade.
function subjectsOfGrades(
  subjects: DownloadSubject[],
  grades: string[],
): DownloadSubject[] {
  if (grades.length === 0) return subjects;
  return subjects.filter((subject) =>
    subject.gradeCodes.some((code) => grades.includes(code)),
  );
}

interface Props {
  options: DownloadOptions;
  value: DownloadFilters;
  parts: DownloadParts;
  // Frozen while a list is being fetched or a run is under way.
  disabled: boolean;
  listing: boolean;
  onChange: (value: DownloadFilters) => void;
  onPartsChange: (parts: DownloadParts) => void;
  onList: () => void;
}

const FiltersCard: React.FC<Props> = ({
  options,
  value,
  parts,
  disabled,
  listing,
  onChange,
  onPartsChange,
  onList,
}) => {
  const subjects = useMemo(
    () => subjectsOfGrades(options.subjects, value.grades),
    [options.subjects, value.grades],
  );
  const gradeChips = useMemo<ChipOption<string>[]>(
    () =>
      options.grades.map((grade) => ({
        value: grade.code,
        label: grade.label,
      })),
    [options.grades],
  );
  const subjectChips = useMemo<ChipOption<number>[]>(
    () =>
      subjects.map((subject) => ({ value: subject.id, label: subject.name })),
    [subjects],
  );
  const nothingChosen =
    value.grades.length + value.subjects.length + value.trimesters.length === 0;

  // A subject the new grades do not offer would keep filtering unseen.
  const toggleGrade = (code: string) => {
    const grades = toggle(value.grades, code);
    const offered = new Set(
      subjectsOfGrades(options.subjects, grades).map((subject) => subject.id),
    );
    onChange({
      ...value,
      grades,
      subjects: value.subjects.filter((id) => offered.has(id)),
    });
  };

  return (
    <Card>
      <Row gutter={[24, 16]}>
        <Col xs={24} lg={8}>
          <ChipGroup
            label="Asignatura"
            clearLabel="Quitar asignatura"
            options={subjectChips}
            selected={value.subjects}
            disabled={disabled}
            onToggle={(id) =>
              onChange({ ...value, subjects: toggle(value.subjects, id) })
            }
            onClear={() => onChange({ ...value, subjects: [] })}
          />
        </Col>
        <Col xs={24} lg={8}>
          <ChipGroup
            label="Grado"
            clearLabel="Quitar grado"
            options={gradeChips}
            selected={value.grades}
            disabled={disabled}
            onToggle={toggleGrade}
            onClear={() => onChange({ ...value, grades: [] })}
          />
        </Col>
        <Col xs={24} lg={8}>
          <ChipGroup
            label="Trimestre"
            clearLabel="Quitar trimestre"
            options={TRIMESTERS}
            selected={value.trimesters}
            disabled={disabled}
            onToggle={(trimester) =>
              onChange({
                ...value,
                trimesters: toggle(value.trimesters, trimester),
              })
            }
            onClear={() => onChange({ ...value, trimesters: [] })}
          />
        </Col>
      </Row>
      <Divider style={{ margin: "20px 0" }} />
      <Flex justify="space-between" align="center" wrap gap={16}>
        <Space size="large" align="center" wrap>
          <Typography.Text strong>Qué descargar</Typography.Text>
          <Checkbox
            checked={parts.od}
            disabled={disabled}
            onChange={(event) =>
              onPartsChange({
                ...parts,
                od: event.target.checked,
                odSummary: false,
              })
            }
          >
            OD
          </Checkbox>
          <Checkbox
            checked={parts.quiz}
            disabled={disabled}
            onChange={(event) =>
              onPartsChange({
                ...parts,
                quiz: event.target.checked,
                odSummary: false,
              })
            }
          >
            Quiz
          </Checkbox>
          <Checkbox
            checked={parts.quizVideo}
            disabled={disabled}
            onChange={(event) =>
              onPartsChange({
                ...parts,
                quizVideo: event.target.checked,
                odSummary: false,
              })
            }
          >
            Quiz de video
          </Checkbox>
          <Checkbox
            checked={parts.odSummary}
            disabled={disabled}
            onChange={(event) =>
              onPartsChange(event.target.checked ? ONLY_OD_SUMMARY : NO_PARTS)
            }
          >
            OD resumida
          </Checkbox>
        </Space>
        {/* A disabled button gets no pointer events, so the span carries them. */}
        <Tooltip title={nothingChosen ? "Elige al menos un filtro" : undefined}>
          <span
            style={{
              display: "inline-block",
              cursor: nothingChosen ? "not-allowed" : undefined,
            }}
          >
            <Button
              loading={listing}
              disabled={nothingChosen || disabled}
              style={nothingChosen ? { pointerEvents: "none" } : undefined}
              onClick={onList}
            >
              Ver cursos
            </Button>
          </span>
        </Tooltip>
      </Flex>
    </Card>
  );
};

export default FiltersCard;
